import { z } from 'zod';
import { createBoardBodySchema } from './board';
import { createCardBodySchema, cardMoveBodySchema, cardTitleSchema } from './card';
import { cardDescriptionPatchSchema } from './card-detail';
import { createListBodySchema } from './list';
import { commentBodySchema } from './comment';
import { checklistItemTextSchema } from './checklist';
import { ulidSchema } from './id';

export const OFFLINE_SYNC_OPERATION_KINDS = [
  'board.create',
  'list.create',
  'card.create',
  'card.move',
  'card.edit_title',
  'card.edit_description',
  'card.add_label',
  'card.add_member',
  'checklist.item_add',
  'comment.add',
] as const;

export const OFFLINE_SYNC_OBJECT_KINDS = [
  'board',
  'list',
  'card',
  'checklist_item',
  'comment',
] as const;

export const OFFLINE_SYNC_RESULT_STATUSES = [
  'applied',
  'rejected',
  'blocked_dependency',
] as const;

export const OFFLINE_SYNC_ERROR_CODES = [
  'blocked_dependency',
  'conflict',
  'forbidden',
  'idempotency_key_reused',
  'not_found',
  'validation_failed',
] as const;

export const CLIENT_OPERATION_ID_MAX_LENGTH = 128;
export const CLIENT_OBJECT_ID_MAX_LENGTH = 128;

const clientIdPattern = /^[A-Za-z0-9._:-]+$/;

export const clientOperationIdSchema = z
  .string()
  .min(1)
  .max(CLIENT_OPERATION_ID_MAX_LENGTH)
  .regex(clientIdPattern, 'must contain only URL-safe opaque id characters');

export const clientObjectIdSchema = z
  .string()
  .min(1)
  .max(CLIENT_OBJECT_ID_MAX_LENGTH)
  .regex(clientIdPattern, 'must contain only URL-safe opaque id characters');

export const offlineSyncObjectKindSchema = z.enum(OFFLINE_SYNC_OBJECT_KINDS);

export const offlineSyncReferenceSchema = z.discriminatedUnion('source', [
  z.strictObject({
    source: z.literal('server'),
    id: ulidSchema,
  }),
  z.strictObject({
    source: z.literal('client'),
    clientObjectId: clientObjectIdSchema,
  }),
]);

const serverObjectReferenceSchema = z.strictObject({
  source: z.literal('server'),
  id: ulidSchema,
});

const clientOperationBase = z.strictObject({
  clientOperationId: clientOperationIdSchema,
});

const createBoardOperationSchema = clientOperationBase.extend({
  kind: z.literal('board.create'),
  clientObjectId: clientObjectIdSchema,
  body: createBoardBodySchema,
});

const createListOperationSchema = clientOperationBase.extend({
  kind: z.literal('list.create'),
  clientObjectId: clientObjectIdSchema,
  board: offlineSyncReferenceSchema,
  body: createListBodySchema.omit({ boardId: true }),
});

const createCardOperationSchema = clientOperationBase.extend({
  kind: z.literal('card.create'),
  clientObjectId: clientObjectIdSchema,
  list: offlineSyncReferenceSchema,
  body: createCardBodySchema.omit({ listId: true }),
});

const moveCardOperationSchema = clientOperationBase.extend({
  kind: z.literal('card.move'),
  card: offlineSyncReferenceSchema,
  list: offlineSyncReferenceSchema,
  body: cardMoveBodySchema.omit({ listId: true }),
});

const editTitleOperationSchema = clientOperationBase.extend({
  kind: z.literal('card.edit_title'),
  card: offlineSyncReferenceSchema,
  title: cardTitleSchema,
  expectedVersion: ulidSchema.nullable().optional(),
});

const editDescriptionOperationSchema = clientOperationBase.extend({
  kind: z.literal('card.edit_description'),
  card: offlineSyncReferenceSchema,
  description: cardDescriptionPatchSchema.shape.description,
  expectedVersion: ulidSchema.nullable().optional(),
});

const addLabelOperationSchema = clientOperationBase.extend({
  kind: z.literal('card.add_label'),
  card: offlineSyncReferenceSchema,
  label: serverObjectReferenceSchema,
});

const addMemberOperationSchema = clientOperationBase.extend({
  kind: z.literal('card.add_member'),
  card: offlineSyncReferenceSchema,
  userId: ulidSchema,
});

const addChecklistItemOperationSchema = clientOperationBase.extend({
  kind: z.literal('checklist.item_add'),
  clientObjectId: clientObjectIdSchema.optional(),
  checklist: offlineSyncReferenceSchema,
  text: checklistItemTextSchema,
});

const addCommentOperationSchema = clientOperationBase.extend({
  kind: z.literal('comment.add'),
  clientObjectId: clientObjectIdSchema.optional(),
  card: offlineSyncReferenceSchema,
  body: commentBodySchema,
});

export const offlineSyncOperationSchema = z.discriminatedUnion('kind', [
  createBoardOperationSchema,
  createListOperationSchema,
  createCardOperationSchema,
  moveCardOperationSchema,
  editTitleOperationSchema,
  editDescriptionOperationSchema,
  addLabelOperationSchema,
  addMemberOperationSchema,
  addChecklistItemOperationSchema,
  addCommentOperationSchema,
]);

export const offlineSyncRequestSchema = z.strictObject({
  operations: z.array(offlineSyncOperationSchema).min(1).max(100),
});

export const offlineSyncIdMappingSchema = z.strictObject({
  objectKind: offlineSyncObjectKindSchema,
  clientObjectId: clientObjectIdSchema,
  serverObjectId: ulidSchema,
});

export const offlineSyncOperationResultSchema = z.strictObject({
  clientOperationId: clientOperationIdSchema,
  operationKind: z.enum(OFFLINE_SYNC_OPERATION_KINDS),
  status: z.enum(OFFLINE_SYNC_RESULT_STATUSES),
  idempotent: z.boolean().default(false),
  objectKind: offlineSyncObjectKindSchema.optional(),
  serverObjectId: ulidSchema.optional(),
  path: z.string().optional(),
  error: z
    .strictObject({
      code: z.enum(OFFLINE_SYNC_ERROR_CODES),
      message: z.string(),
      details: z.record(z.string(), z.array(z.string())).optional(),
    })
    .optional(),
});

export const offlineSyncResponseSchema = z.strictObject({
  results: z.array(offlineSyncOperationResultSchema),
  mappings: z.array(offlineSyncIdMappingSchema),
});

export type OfflineSyncOperation = z.infer<typeof offlineSyncOperationSchema>;
export type OfflineSyncRequest = z.infer<typeof offlineSyncRequestSchema>;
export type OfflineSyncReference = z.infer<typeof offlineSyncReferenceSchema>;
export type OfflineSyncIdMapping = z.infer<typeof offlineSyncIdMappingSchema>;
export type OfflineSyncOperationResult = z.infer<typeof offlineSyncOperationResultSchema>;
export type OfflineSyncResponse = z.infer<typeof offlineSyncResponseSchema>;
